package link.erpango.quip.domain.noteimport

import kotlinx.datetime.LocalDate
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.VehicleType
import link.erpango.quip.domain.extraction.ExtractionConfidence
import link.erpango.quip.domain.extraction.SourceSpan

/**
 * Editable review model of the "importar desde nota" assistant.
 *
 * The parser ([link.erpango.quip.domain.extraction.parseNote]) and the backend LLM both produce
 * candidates; they are mapped into an [ImportDraft], merged, and shown to the user, who edits
 * and confirms what is actually created. Nothing in here is ever saved without that review.
 */
object NoteImportLimits {
    /** Same default as the backend (EXTRACTION_NOTE_MAX_LENGTH); the client never sends more. */
    const val MAX_NOTE_LENGTH = 2000
}

/** Where a suggested value came from; [USER] is a value typed in the bulk review. */
enum class FieldSource { NOTE, AI, BOTH, USER }

/**
 * A suggested value for one form field.
 *
 * @property span where the value was found in the note (only for [FieldSource.NOTE] / [FieldSource.BOTH]),
 *   so the UI can quote it.
 * @property alternative a different value the other source proposed ("¿O quizá X?").
 */
data class SuggestedField<T>(
    val value: T,
    val confidence: ExtractionConfidence,
    val source: FieldSource,
    val span: SourceSpan? = null,
    val alternative: T? = null,
)

/** Which vehicle a maintenance or spare part belongs to. */
sealed interface VehicleRef {
    /** A vehicle detected in the same note ([VehicleDraft.id]). */
    data class Draft(val draftId: String) : VehicleRef

    /** A vehicle already in the user's garage. */
    data class Garage(val vin: String) : VehicleRef

    /** Not associated yet ("asignar después"). */
    data object Unassigned : VehicleRef
}

data class VehicleDraft(
    val id: String,
    val vin: SuggestedField<String>? = null,
    val plate: SuggestedField<String>? = null,
    val brand: SuggestedField<String>? = null,
    val model: SuggestedField<String>? = null,
    val year: SuggestedField<Int>? = null,
    /** A [link.erpango.quip.theme.VehicleColors] id ("grey", "blue_navy"...). */
    val color: SuggestedField<String>? = null,
    val owner: SuggestedField<String>? = null,
    val vehicleType: SuggestedField<VehicleType>? = null,
    val engineOil: SuggestedField<String>? = null,
    val frontTireSize: SuggestedField<String>? = null,
    val rearTireSize: SuggestedField<String>? = null,
    /** One of [link.erpango.quip.QuipConstants.VehicleAttributes.FuelTypes]. */
    val recommendedFuel: SuggestedField<String>? = null,
    val engineDetails: SuggestedField<String>? = null,
    val cylinderCount: SuggestedField<Int>? = null,
    val frontBulbType: SuggestedField<String>? = null,
    val rearBulbType: SuggestedField<String>? = null,
    val batterySpecs: SuggestedField<String>? = null,
    val coolantType: SuggestedField<String>? = null,
    val frontTirePressureBelow160: SuggestedField<Double>? = null,
    val rearTirePressureBelow160: SuggestedField<Double>? = null,
    val frontTirePressureAbove160: SuggestedField<Double>? = null,
    val rearTirePressureAbove160: SuggestedField<Double>? = null,
    /** Epoch millis at UTC midnight, like [MaintenanceDraft.date]. */
    val firstRegistrationDate: SuggestedField<Long>? = null,
    /** Epoch millis at UTC midnight, like [MaintenanceDraft.date]. */
    val nextItvDate: SuggestedField<Long>? = null,
    /** Set when this vehicle is already in the garage (same VIN or plate). */
    val garageMatchVin: String? = null,
)

/** How precise a normalized date is: "12/03/2025" is a day, "marzo 2025" only a month. */
enum class DatePrecision { DAY, MONTH }

data class MaintenanceDraft(
    val id: String,
    val description: SuggestedField<String>? = null,
    /** Epoch millis at UTC midnight, the same convention as the Material date picker. */
    val date: SuggestedField<Long>? = null,
    val datePrecision: DatePrecision? = null,
    /** The date as written in the note ("marzo 2025"), shown when it could not be normalized. */
    val rawDate: String? = null,
    val kilometers: SuggestedField<Int>? = null,
    val cost: SuggestedField<Double>? = null,
    /** Workshop name read from an invoice header. */
    val workshop: SuggestedField<String>? = null,
    /** Mechanic name read from an invoice header. */
    val mechanic: SuggestedField<String>? = null,
    val vehicle: VehicleRef = VehicleRef.Unassigned,
)

data class SparePartDraft(
    val id: String,
    /** The phrase that triggered the detection ("filtro de aceite"), when it came from the note. */
    val phrase: String? = null,
    val categoryId: SuggestedField<String>? = null,
    val typeId: SuggestedField<String>? = null,
    val brand: SuggestedField<String>? = null,
    val model: SuggestedField<String>? = null,
    val reference: SuggestedField<String>? = null,
    val quantity: SuggestedField<Double>? = null,
    val unit: SuggestedField<String>? = null,
    val vehicle: VehicleRef = VehicleRef.Unassigned,
)

data class ImportDraft(
    val vehicles: List<VehicleDraft> = emptyList(),
    val maintenances: List<MaintenanceDraft> = emptyList(),
    val spareParts: List<SparePartDraft> = emptyList(),
    /** True once an AI result has been merged in. */
    val aiApplied: Boolean = false,
) {
    val isEmpty: Boolean get() = vehicles.isEmpty() && maintenances.isEmpty() && spareParts.isEmpty()

    fun vehicle(id: String): VehicleDraft? = vehicles.firstOrNull { it.id == id }
}

/** Catalog data the mappers need to canonicalize values. */
data class DraftContext(
    val today: LocalDate,
    val brands: List<String>,
    val sparePartCatalog: DSparePartCatalog,
)

internal fun ExtractionConfidence.atMost(other: ExtractionConfidence): ExtractionConfidence =
    if (ordinal >= other.ordinal) this else other

internal fun ExtractionConfidence.atLeast(other: ExtractionConfidence): ExtractionConfidence =
    if (ordinal <= other.ordinal) this else other
